import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/client';
import { BottomNav } from '../../components/app/DarkTheme';
import { 
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid 
} from 'recharts';
import html2canvas from 'html2canvas';
import { Download, Info, X } from 'lucide-react';
import { toast } from 'sonner';

// Category colors - vibrant on dark
const CATS = {
  strength:           { color: '#EF4444', name: '근력', key: 'strength_score' },
  cardio:             { color: '#3B82F6', name: '심폐지구력', key: 'cardio_score' },
  body_composition:   { color: '#A78BFA', name: '체성분', key: 'body_composition_score' },
  body_shape:         { color: '#22C55E', name: '체형', key: 'flexibility_score' },
  muscular_endurance: { color: '#F97316', name: '근지구력', key: 'muscular_endurance_score' },
};
// CRM order: 근력 → 심폐지구력 → 체성분 → 체형 → 근지구력 (clockwise from top)
const CAT_ORDER = ['strength', 'cardio', 'body_composition', 'body_shape', 'muscular_endurance'];
// 항목별 점수 표시 순서: 근력 → 근지구력 → 심폐지구력 → 체성분 → 체형
const SCORE_DISPLAY_ORDER = ['strength', 'muscular_endurance', 'cardio', 'body_composition', 'body_shape'];

// Exercise code → 한글 라벨 매핑 (CRM BatchEntry와 동기화)
const EXERCISE_LABELS = {
  deadlift: '데드리프트',
  squat: '스쿼트',
  bench_press: '벤치프레스',
  shoulder_press: '숄더프레스',
  pushup: '푸시업',
  situp: '싯업',
  barbell_squat_endurance: '바벨 스쿼트',
  barbell_deadlift_endurance: '바벨 데드리프트',
  barbell_shoulder_press_endurance: '바벨 숄더프레스',
  plank: '플랭크',
  wall_squat: '월 스쿼트',
  amrap1: 'AMRAP1',
  amrap2: 'AMRAP2',
  amrap3: 'AMRAP3',
  amrap4: 'AMRAP4',
  running: '러닝',
  rowing: '로잉 (퍼포먼스 기록)',
  inbody: '인바디',
  posture: '자세평가',
};
const getExerciseLabel = (code) => EXERCISE_LABELS[code] || code;

// Default level palette - colors mapped by POSITION in the list (1st=red, 2nd=amber, ...).
// The NAMES come from the admin-configured /admin/score-levels endpoint.
const LEVEL_COLORS = ['#EF4444', '#F59E0B', '#22C55E', '#3B82F6', '#A78BFA', '#EC4899', '#10B981'];

const getScoreStatus = (score, levels) => {
  if (!score || score === 0) return { text: '-', color: '#52525B' };
  if (!Array.isArray(levels) || levels.length === 0) {
    return { text: '-', color: '#52525B' };
  }
  // levels are ascending by min_score; pick the highest level whose min_score <= score
  let matchedIdx = 0;
  for (let i = 0; i < levels.length; i++) {
    if (score >= levels[i].min_score) matchedIdx = i;
  }
  const level = levels[matchedIdx];
  const color = LEVEL_COLORS[matchedIdx] || LEVEL_COLORS[LEVEL_COLORS.length - 1];
  return { text: level.name, color };
};

const MOTIVATIONAL_MESSAGES = [
  '오늘도 나를 위한 시간',
  '잘 하고 있어요 👍',
  '오늘도 성장 중, 훌륭해요',
  '지금 이 흐름, 정말 좋아요',
  '오늘 느낌 괜찮은데요? 😎',
  '내일의 나를 위해, 오늘도 화이팅 💪',
  '어제보다 강한 오늘, 멋져요 😍',
  '매일 조금씩 분명히 변화되고 있어요!',
  '오늘의 땀은 내일의 자신감이 돼요',
  '포기하지 않는 우리, 이미 대단해요 👏',
  '좋은 습관과 변화, 정말 의미 있어요 ☺️',
];
const getRandomMotivation = () => MOTIVATIONAL_MESSAGES[Math.floor(Math.random() * MOTIVATIONAL_MESSAGES.length)];

// ─── Custom SVG Radar ───
const RadarChartCustom = ({ snapshot, averages, showAvg }) => {
  const size = 280;
  const center = size / 2;
  const maxRadius = 80;
  const levels = [25, 50, 75, 100];
  const n = 5;

  const getPoint = useCallback((idx, val) => {
    const angle = (Math.PI * 2 * idx) / n - Math.PI / 2;
    const r = (Math.min(val, 100) / 100) * maxRadius;
    return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) };
  }, [center, maxRadius, n]);

  const scores = CAT_ORDER.map(k => snapshot?.[CATS[k].key] || 0);
  const avgMap = { strength: 'strength', muscular_endurance: 'muscular_endurance', cardio: 'cardio', body_shape: 'balance', body_composition: 'body_composition' };
  const avgScores = CAT_ORDER.map(k => averages?.[avgMap[k]] ?? null);

  const dataPoints = scores.map((s, i) => getPoint(i, s));
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';

  let avgPath = null;
  if (showAvg) {
    const validAvg = avgScores.map((s, i) => s != null ? getPoint(i, s) : getPoint(i, 0));
    if (avgScores.some(s => s != null)) {
      avgPath = validAvg.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';
    }
  }

  const outerPoints = Array.from({ length: n }, (_, i) => getPoint(i, 100));
  const outerPath = outerPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';

  // Per-vertex label positions (항목별 미세 offset)
  // [labelDist, scoreDist, anchor, baslineShift]
  const labelConfig = [
    { ld: maxRadius + 30, sd: maxRadius + 16, anchor: 'middle' },  // 0: 근력 (top)
    { ld: maxRadius + 28, sd: maxRadius + 14, anchor: 'start' },   // 1: 심폐지구력 (top-right)
    { ld: maxRadius + 26, sd: maxRadius + 12, anchor: 'start' },   // 2: 체성분 (bottom-right)
    { ld: maxRadius + 26, sd: maxRadius + 12, anchor: 'end' },     // 3: 체형 (bottom-left)
    { ld: maxRadius + 28, sd: maxRadius + 14, anchor: 'end' },     // 4: 근지구력 (top-left)
  ];

  const getLabelPos = (idx, dist) => {
    const angle = (Math.PI * 2 * idx) / n - Math.PI / 2;
    return { x: center + dist * Math.cos(angle), y: center + dist * Math.sin(angle) };
  };

  return (
    <svg width="100%" viewBox={`0 0 ${size} ${size}`} className="mx-auto" style={{ maxWidth: 300 }}>
      <defs>
        <radialGradient id="radarFill" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FF7A00" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#FF7A00" stopOpacity="0.06" />
        </radialGradient>
      </defs>

      {/* Background pentagon */}
      <path d={outerPath} fill="#1E2128" stroke="#2E323C" strokeWidth="1" />

      {/* Grid levels */}
      {levels.map(level => {
        const pts = Array.from({ length: n }, (_, i) => getPoint(i, level));
        const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';
        return <path key={level} d={d} fill="none" stroke="#2E323C" strokeWidth="0.5" />;
      })}

      {/* Axis lines */}
      {Array.from({ length: n }, (_, i) => {
        const p = getPoint(i, 100);
        return <line key={i} x1={center} y1={center} x2={p.x} y2={p.y} stroke="#2E323C" strokeWidth="0.5" />;
      })}

      {/* Data fill (내 점수) */}
      <path d={dataPath} fill="url(#radarFill)" stroke="#FF7A00" strokeWidth="2" />

      {/* Average overlay (항상 렌더, visibility 제어) */}
      <path
        d={avgPath || outerPath}
        fill="none"
        stroke="#7CB3F4"
        strokeWidth="1.5"
        strokeDasharray="5 3"
        opacity="0.7"
        visibility={avgPath ? 'visible' : 'hidden'}
      />

      {/* 내 점수 vertex dots */}
      {dataPoints.map((p, i) => (
        <g key={`dot-${i}`}>
          {scores[i] > 0 && <circle cx={p.x} cy={p.y} r="3" fill="#0F1115" stroke="#FF7A00" strokeWidth="1.5" />}
        </g>
      ))}

      {/* Average vertex dots */}
      {avgScores.map((s, i) => {
        if (!showAvg || s == null) return <g key={`avgd-${i}`} />;
        const p = getPoint(i, s);
        return <circle key={`avgd-${i}`} cx={p.x} cy={p.y} r="2.5" fill="#7CB3F4" opacity="0.8" />;
      })}

      {/* Labels + Scores per vertex (SVG foreignObject 대신 순수 SVG text 사용하지 않음 — 아래 HTML overlay에서 처리) */}
    </svg>
  );
};

// HTML label + score overlay (CRM 스타일: 점수는 항목명 바로 아래, 근력만 우측)
const RadarLabels = ({ snapshot }) => {
  const cats = CAT_ORDER.map(k => CATS[k]);
  const scores = CAT_ORDER.map(k => snapshot?.[CATS[k].key] || 0);

  const config = [
    { lx: 50, ly: 8,  ltx: '-50%', lty: '0' },      // 0: 근력 (top) — 유지
    { lx: 78, ly: 36, ltx: '0',    lty: '-50%' },    // 1: 심폐지구력 (top-right) — 더 가깝게
    { lx: 70, ly: 78, ltx: '0',    lty: '0' },       // 2: 체성분 (bottom-right) — 더 가깝게
    { lx: 30, ly: 78, ltx: '-100%',lty: '0' },       // 3: 체형 (bottom-left) — 더 가깝게
    { lx: 22, ly: 36, ltx: '-100%',lty: '-50%' },    // 4: 근지구력 (top-left) — 더 가깝게
  ];

  return (
    <>
      {cats.map((cat, i) => {
        const c = config[i];
        const hasData = scores[i] > 0;
        const scoreText = hasData ? scores[i].toFixed(0) : '-';
        const scoreColor = hasData ? cat.color : '#3A3F4A';

        if (i === 0) {
          return (
            <div key={cat.name} className="absolute pointer-events-none" style={{ left: `${c.lx}%`, top: `${c.ly}%`, transform: `translate(${c.ltx}, ${c.lty})` }}>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-medium whitespace-nowrap text-[#9CA3AF]">{cat.name}</span>
                <span className="text-[13px] font-bold whitespace-nowrap" style={{ color: scoreColor }}>{scoreText}</span>
              </div>
            </div>
          );
        }

        return (
          <div key={cat.name} className="absolute pointer-events-none" style={{ left: `${c.lx}%`, top: `${c.ly}%`, transform: `translate(${c.ltx}, ${c.lty})` }}>
            <div className="text-[11px] font-medium whitespace-nowrap text-[#9CA3AF] text-center">{cat.name}</div>
            <div className="text-[13px] font-bold whitespace-nowrap text-center" style={{ color: scoreColor }}>{scoreText}</div>
          </div>
        );
      })}
    </>
  );
};

// ─── Main Component ───
const MemberScore = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scoreHistory, setScoreHistory] = useState({ exercise_history: [], body_history: [] });
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  const [historyTab, setHistoryTab] = useState('exercise');
  const [periodMonths, setPeriodMonths] = useState(6);
  const [selectedPoint, setSelectedPoint] = useState(null);
  // Chart selection state: { date, dataKey } — tracks which dot is selected
  const [chartSelection, setChartSelection] = useState(null);

  const [ageAverages, setAgeAverages] = useState(null);
  const [showAvg, setShowAvg] = useState(false);
  const [scoreLevels, setScoreLevels] = useState([]);
  const [levelInfoOpen, setLevelInfoOpen] = useState(false);

  // Image capture
  const captureRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);

  // Line visibility toggles
  const [visibleLines, setVisibleLines] = useState({
    strength: true, muscular_endurance: true, cardio: true,
    body_composition: true, body_shape: true,
  });
  const toggleLine = (key) => setVisibleLines(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (!token) { navigate('/app/login'); return; }
    fetchData();
    fetchScoreLevels();
    setTimeout(() => setMounted(true), 50);
  }, [token, navigate]);

  useEffect(() => { fetchScoreHistory(); }, [periodMonths, token]);

  const fetchScoreLevels = async () => {
    try { const r = await apiClient.get('/app/score-levels'); setScoreLevels(r.data.levels || []); } catch {}
  };
  const hasAvgData = ageAverages && Object.values(ageAverages).some(v => v != null);

  const handleSaveImage = async () => {
    if (!captureRef.current || isSaving) return;
    setIsSaving(true);
    try {
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: '#0F1115',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.download = `fitness-score-${today}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('이미지가 저장되었습니다');
    } catch (err) {
      console.error('Save image failed:', err);
      toast.error('이미지 저장 실패');
    } finally {
      setIsSaving(false);
    }
  };

  const fetchData = async () => {
    try {
      const [sr, mr] = await Promise.all([
        apiClient.get('/app/fitness-score', { params: { token } }),
        apiClient.get('/app/measurements', { params: { token } })
      ]);
      setSnapshot(sr.data);
      setAgeAverages(sr.data?.age_averages || null);
      setMeasurements(mr.data || []);
      if (mr.data?.length > 0) setSelectedPoint(mr.data[0]);
    } catch {} finally { setLoading(false); }
  };

  const fetchScoreHistory = async () => {
    try {
      const r = await apiClient.get('/app/score-history', { params: { token, months: periodMonths } });
      setScoreHistory(r.data);
    } catch {}
  };

  // Auto-select most recent point when tab/period/data changes
  useEffect(() => {
    const history = historyTab === 'exercise' ? scoreHistory.exercise_history : scoreHistory.body_history;
    if (!history || history.length === 0) {
      setChartSelection(null);
      return;
    }
    // Most recent entry (last in array)
    const latest = history[history.length - 1];
    const dt = latest.date;
    // Find which dataKey has a value
    const keys = historyTab === 'exercise'
      ? ['strength', 'muscular_endurance', 'cardio']
      : ['body_composition', 'body_shape'];
    const activeKey = keys.find(k => latest[k] != null) || keys[0];
    setChartSelection({ date: dt, dataKey: activeKey });
    // Match to measurement
    const catMap = { strength: 'strength', muscular_endurance: 'muscular_endurance', cardio: 'cardio', body_composition: 'inbody', body_shape: 'posture' };
    const matchCat = catMap[activeKey];
    const m = measurements.find(me => {
      if (!me.measured_at) return false;
      const mDate = typeof me.measured_at === 'string' ? me.measured_at.slice(0, 10) : '';
      return mDate === dt && (!matchCat || me.category === matchCat);
    }) || measurements.find(me => {
      if (!me.measured_at) return false;
      return (typeof me.measured_at === 'string' ? me.measured_at.slice(0, 10) : '') === dt;
    });
    if (m) setSelectedPoint(m);
  }, [historyTab, scoreHistory, measurements]);

  const totalScore = useMemo(() => {
    if (!snapshot) return null;
    const s = CAT_ORDER.map(k => snapshot[CATS[k].key]).filter(v => v && v > 0);
    return s.length === 0 ? null : Math.round(s.reduce((a, b) => a + b, 0) / s.length);
  }, [snapshot]);

  const [motivation] = useState(() => getRandomMotivation());

  const periodOptions = [
    { value: 1, label: '1개월' }, { value: 3, label: '3개월' },
    { value: 6, label: '6개월' }, { value: 12, label: '1년' }, { value: 0, label: '전체' }
  ];

  const formatDate = (d) => { if (!d) return ''; const x = new Date(d); return `${x.getMonth()+1}/${x.getDate()}`; };
  const formatFull = (d) => { if (!d) return ''; const x = new Date(d); return `${x.getFullYear()}.${x.getMonth()+1}.${x.getDate()}`; };

  const handleDotClick = (dataKey, date) => {
    const catMap = { strength: 'strength', muscular_endurance: 'muscular_endurance', cardio: 'cardio', body_composition: 'inbody', body_shape: 'posture' };
    const matchCat = catMap[dataKey];
    setChartSelection({ date, dataKey });
    
    const f = measurements.find(m => {
      if (!m.measured_at) return false;
      const mDate = typeof m.measured_at === 'string' ? m.measured_at.slice(0, 10) : '';
      return mDate === date && (!matchCat || m.category === matchCat);
    }) || measurements.find(m => {
      if (!m.measured_at) return false;
      return (typeof m.measured_at === 'string' ? m.measured_at.slice(0, 10) : '') === date;
    });
    
    if (f) {
      setSelectedPoint(f);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="measurement-detail"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const getCatName = (c) => ({ strength:'근력', muscular_endurance:'근지구력', cardio:'심폐지구력', inbody:'체성분', posture:'체형', flexibility:'체형' }[c] || c);

  // Custom dot with selection state
  const ClickableDot = ({ cx, cy, payload, dataKey, color }) => {
    if (cx == null || cy == null || !payload?.[dataKey]) return null;
    const isSelected = chartSelection?.date === payload.date && chartSelection?.dataKey === dataKey;
    return (
      <g style={{ cursor: 'pointer' }} onClick={() => handleDotClick(dataKey, payload.date)}>
        {isSelected && <circle cx={cx} cy={cy} r={10} fill={color} opacity={0.15} />}
        <circle cx={cx} cy={cy} r={isSelected ? 5 : 4} fill={isSelected ? color : '#1A1D23'} stroke={color} strokeWidth={2} />
      </g>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-[#0F1115]">
      <div className="w-8 h-8 border-2 border-[#FF7A00]/30 border-t-[#FF7A00] rounded-full animate-spin" />
    </div>
  );

  const anim = (delay) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(20px)',
    transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
  });

  return (
    <div className="min-h-screen bg-[#0F1115] pb-24">
      {/* Inline styles for animations */}
      <style>{`
        @keyframes pulse-slow { 0%,100%{r:5;opacity:0.2} 50%{r:8;opacity:0.4} }
        .animate-pulse-slow { animation: pulse-slow 2.5s ease-in-out infinite; }
        @keyframes glow-pulse { 0%,100%{opacity:0.4;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.1)} }
        .animate-glow-pulse { animation: glow-pulse 3s ease-in-out infinite; }
        @keyframes bar-fill { from{width:0%} }
        .animate-bar-fill { animation: bar-fill 0.8s cubic-bezier(0.16,1,0.3,1) forwards; }
        @keyframes fade-in { from{opacity:0} to{opacity:1} }
        .animate-fade-in { animation: fade-in 0.18s ease-out forwards; }
        @keyframes slide-up { from{transform:translateY(100%);opacity:0.4} to{transform:translateY(0);opacity:1} }
        .animate-slide-up { animation: slide-up 0.28s cubic-bezier(0.16,1,0.3,1) forwards; }
        .recharts-wrapper, .recharts-wrapper svg,
        .recharts-surface, .recharts-layer,
        .recharts-line, .recharts-line-dots,
        .recharts-tooltip-wrapper,
        .recharts-wrapper *:focus,
        .recharts-wrapper *:active {
          outline: none !important;
          box-shadow: none !important;
          border: none !important;
          -webkit-tap-highlight-color: transparent !important;
          -webkit-focus-ring-color: transparent !important;
        }
        .recharts-wrapper { -webkit-tap-highlight-color: transparent; }
      `}</style>

      {/* Header */}
      <div className="px-5 pt-12 pb-2 flex items-center justify-between" style={anim(0)}>
        <h1 className="text-xl font-bold text-white">피트니스 점수</h1>
        <button
          onClick={handleSaveImage}
          disabled={isSaving}
          className="flex items-center gap-1.5 text-xs text-[#A1A1AA] bg-[#1A1D23] hover:bg-[#22252E] px-3 py-2 rounded-full transition-colors disabled:opacity-50"
          data-testid="save-image-btn"
          aria-label="이미지 저장"
        >
          <Download className="h-3.5 w-3.5" />
          {isSaving ? '저장 중...' : '이미지 저장'}
        </button>
      </div>

      <div className="px-4 space-y-4">
        {/* ═══ CAPTURE ZONE START ═══ */}
        <div ref={captureRef} className="space-y-4 pb-2 rounded-2xl">
        {/* ═══ 1. HERO SCORE CARD ═══ */}
        <div data-testid="total-score-card" className="relative rounded-2xl overflow-hidden" style={anim(0.05)}>
          {/* Background layers */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1A1D23] via-[#1A1D23] to-[#22252E]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,122,0,0.08)_0%,_transparent_70%)]" />
          
          {/* Glow orb */}
          {totalScore && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-[#FF7A00]/10 rounded-full blur-3xl animate-glow-pulse" />
          )}

          <div className="relative py-10 px-6 text-center">
            {totalScore !== null ? (
              <>
                <p className="text-[#52525B] text-xs tracking-wider uppercase mb-3">종합 점수</p>
                <div className="relative inline-block">
                  <span className="text-7xl font-black tracking-tight" style={{ color: '#FF7A00', textShadow: '0 0 40px rgba(255,122,0,0.3)' }}>
                    {totalScore}
                  </span>
                  <span className="text-2xl font-medium text-[#71717A] ml-1">점</span>
                </div>
                <p className="mt-3 text-sm font-medium text-[#FF8C42]">
                  {motivation}
                </p>
                <p className="text-[10px] text-[#3A3F4A] mt-2">
                  최근 측정 데이터를 기반으로 자동 계산된 점수입니다
                </p>
              </>
            ) : (
              <>
                <p className="text-[#52525B] text-xs tracking-wider uppercase mb-3">종합 점수</p>
                <div className="text-7xl font-black text-[#1A1D23]">-</div>
                <p className="text-xs text-[#3A3F4A] mt-3">측정 데이터가 없습니다</p>
              </>
            )}
          </div>
        </div>

        {/* ═══ 2. RADAR CHART ═══ */}
        <div data-testid="radar-chart-card" className="bg-[#1A1D23] rounded-2xl pt-4 pb-3 px-3 relative" style={anim(0.1)}>
          {/* Toggle */}
          {hasAvgData && (
            <button
              onClick={() => setShowAvg(!showAvg)}
              data-testid="toggle-average"
              className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-medium z-10 transition-all duration-200 ${
                showAvg
                  ? 'bg-[#3B82F6]/15 text-[#60A5FA] border border-[#3B82F6]/30'
                  : 'bg-[#22252E] text-[#52525B] border border-[#2A2E38] hover:text-[#71717A]'
              }`}
            >
              {showAvg ? '평균 숨기기' : '연령 평균'}
            </button>
          )}

          <h2 className="text-white text-sm font-semibold text-center">종합 밸런스</h2>

          {/* Legend */}
          <div className="flex justify-center gap-4 mt-1 mb-0">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#FF7A00]" />
              <span className="text-[10px] text-[#52525B]">내 점수</span>
            </div>
            {showAvg && hasAvgData && (
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[#3B82F6]" />
                <span className="text-[10px] text-[#52525B]">연령 평균</span>
              </div>
            )}
          </div>

          <div className="relative">
            <RadarChartCustom snapshot={snapshot} averages={ageAverages} showAvg={showAvg && hasAvgData} />
            <RadarLabels snapshot={snapshot} />
          </div>
        </div>

        {/* ═══ 3. CATEGORY SCORES ═══ */}
        <div data-testid="category-scores" className="space-y-2" style={anim(0.15)}>
          <h2 className="text-white text-sm font-semibold px-1 mb-1">항목별 점수</h2>
          {SCORE_DISPLAY_ORDER.map((cat, idx) => {
            const catInfo = CATS[cat];
            const score = snapshot?.[catInfo.key] || 0;
            const status = getScoreStatus(score, scoreLevels);
            const barW = score > 0 ? Math.min(score, 100) : 0;

            return (
              <div
                key={cat}
                className="bg-[#1A1D23] rounded-xl p-4 hover:bg-[#1E2128] transition-colors duration-200"
                style={{ animationDelay: `${idx * 0.05}s` }}
              >
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: catInfo.color, boxShadow: `0 0 8px ${catInfo.color}40` }} />
                    <span className="text-[#A1A1AA] text-sm font-medium">{catInfo.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold tabular-nums" style={{ color: score > 0 ? catInfo.color : '#2A2E38' }}>
                      {score > 0 ? score.toFixed(0) : '-'}
                    </span>
                    {/* Level badge: 근력/근지구력/심폐지구력에만 표시. 체성분/체형은 상태 참고용이라 제외. */}
                    {['strength', 'muscular_endurance', 'cardio'].includes(cat) && (
                      <button
                        type="button"
                        onClick={() => setLevelInfoOpen(true)}
                        data-testid={`level-badge-${cat}`}
                        className="flex items-center gap-1 text-[10px] font-medium pl-1.5 pr-1 py-0.5 rounded transition-transform active:scale-95"
                        style={{ color: status.color, backgroundColor: `${status.color}15` }}
                        aria-label={`${status.text} 레벨 안내 보기`}
                      >
                        <span>{status.text}</span>
                        <Info className="w-3 h-3 opacity-80" />
                      </button>
                    )}
                  </div>
                </div>
                {/* Progress bar */}
                <div className="h-1.5 bg-[#22252E] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full animate-bar-fill"
                    style={{
                      width: `${barW}%`,
                      background: `linear-gradient(90deg, ${catInfo.color}90, ${catInfo.color})`,
                      animationDelay: `${0.3 + idx * 0.1}s`,
                      animationFillMode: 'backwards',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* ═══ CAPTURE ZONE END ═══ */}
        </div>

        {/* ═══ 4. TREND CHART ═══ */}
        <div data-testid="trend-chart" className="bg-[#1A1D23] rounded-2xl p-4" style={anim(0.2)}>
          <h2 className="text-white text-sm font-semibold mb-3">변화 추이</h2>

          {/* Tabs */}
          <div className="flex bg-[#0F1115] rounded-xl p-1 mb-3">
            {[{ key: 'exercise', label: '운동 기록' }, { key: 'body', label: '측정 기록' }].map(t => (
              <button
                key={t.key}
                onClick={() => setHistoryTab(t.key)}
                className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                  historyTab === t.key
                    ? 'bg-[#FF7A00] text-white shadow-[0_2px_8px_rgba(255,122,0,0.3)]'
                    : 'text-[#52525B] hover:text-[#71717A]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Period */}
          <div className="flex gap-1.5 mb-4 overflow-x-auto scrollbar-hide">
            {periodOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setPeriodMonths(opt.value)}
                className={`px-3 py-1 text-[10px] rounded-full whitespace-nowrap transition-all duration-200 ${
                  periodMonths === opt.value
                    ? 'bg-[#FF7A00]/15 text-[#FF8C42] border border-[#FF7A00]/30'
                    : 'bg-[#0F1115] text-[#52525B] border border-transparent hover:text-[#71717A]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Line toggles */}
          <div className="flex flex-wrap gap-2 mb-4">
            {(historyTab === 'exercise'
              ? [{ key: 'strength', cat: CATS.strength }, { key: 'muscular_endurance', cat: CATS.muscular_endurance }, { key: 'cardio', cat: CATS.cardio }]
              : [{ key: 'body_composition', cat: CATS.body_composition }, { key: 'body_shape', cat: CATS.body_shape }]
            ).map(({ key, cat }) => (
              <button
                key={key}
                data-testid={`toggle-line-${key}`}
                onClick={() => toggleLine(key)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all duration-200 ${
                  visibleLines[key]
                    ? 'bg-[#0F1115] border border-[#2A2E38]'
                    : 'bg-[#0F1115] border border-[#1A1D23] opacity-40'
                }`}
              >
                <div className="w-2 h-2 rounded-full transition-opacity" style={{ backgroundColor: cat.color, opacity: visibleLines[key] ? 1 : 0.3 }} />
                <span style={{ color: visibleLines[key] ? cat.color : '#3A3F4A' }}>{cat.name}</span>
              </button>
            ))}
          </div>

          {/* Chart content */}
          {historyTab === 'exercise' ? (
            scoreHistory.exercise_history.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={scoreHistory.exercise_history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1A1D23" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: '#3A3F4A' }} interval="preserveStartEnd" axisLine={{ stroke: '#1A1D23' }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#3A3F4A' }} width={28} axisLine={false} tickLine={false} />
                  {visibleLines.strength && <Line type="monotone" dataKey="strength" stroke={CATS.strength.color} strokeWidth={2} dot={<ClickableDot dataKey="strength" color={CATS.strength.color} />} activeDot={false} isAnimationActive={false} connectNulls />}
                  {visibleLines.muscular_endurance && <Line type="monotone" dataKey="muscular_endurance" stroke={CATS.muscular_endurance.color} strokeWidth={2} dot={<ClickableDot dataKey="muscular_endurance" color={CATS.muscular_endurance.color} />} activeDot={false} isAnimationActive={false} connectNulls />}
                  {visibleLines.cardio && <Line type="monotone" dataKey="cardio" stroke={CATS.cardio.color} strokeWidth={2} dot={<ClickableDot dataKey="cardio" color={CATS.cardio.color} />} activeDot={false} isAnimationActive={false} connectNulls />}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-[#2A2E38] text-sm">운동 기록이 없습니다</div>
            )
          ) : (
            scoreHistory.body_history.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={scoreHistory.body_history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1A1D23" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: '#3A3F4A' }} interval="preserveStartEnd" axisLine={{ stroke: '#1A1D23' }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#3A3F4A' }} width={28} axisLine={false} tickLine={false} />
                  {visibleLines.body_composition && <Line type="monotone" dataKey="body_composition" stroke={CATS.body_composition.color} strokeWidth={2} dot={<ClickableDot dataKey="body_composition" color={CATS.body_composition.color} />} activeDot={false} isAnimationActive={false} connectNulls />}
                  {visibleLines.body_shape && <Line type="monotone" dataKey="body_shape" stroke={CATS.body_shape.color} strokeWidth={2} dot={<ClickableDot dataKey="body_shape" color={CATS.body_shape.color} />} activeDot={false} isAnimationActive={false} connectNulls />}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-[#2A2E38] text-sm">측정 기록이 없습니다</div>
            )
          )}
        </div>

        {/* ═══ 5. MEASUREMENT DETAIL ═══ */}
        {selectedPoint && (
          <div data-testid="measurement-detail" className="bg-[#1A1D23] rounded-2xl p-4" style={anim(0.25)}>
            <h2 className="text-white text-sm font-semibold mb-3">측정 세부 정보</h2>
            <div className="bg-[#0F1115] rounded-xl p-4 border border-[#22252E]">
              <div className="flex justify-between items-start">
                <div>
                  <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold"
                    style={{
                      backgroundColor: `${CATS[selectedPoint.category]?.color || CATS.body_composition.color}15`,
                      color: CATS[selectedPoint.category]?.color || CATS.body_composition.color
                    }}>
                    {getCatName(selectedPoint.category)}
                  </span>
                  {selectedPoint.exercise && (
                    <p className="font-medium text-white text-sm mt-2">{getExerciseLabel(selectedPoint.exercise)}</p>
                  )}
                  <p className="text-[10px] text-[#3A3F4A] mt-1">{formatFull(selectedPoint.measured_at)}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold" style={{ color: '#FF7A00', textShadow: '0 0 20px rgba(255,122,0,0.2)' }}>
                    {selectedPoint.score?.toFixed(0) || '-'}
                  </div>
                  <span className="text-[10px] text-[#52525B]">점</span>
                </div>
              </div>

              {(selectedPoint.raw_metrics || selectedPoint.computed_metrics) && (() => {
                const rm = selectedPoint.raw_metrics || {};
                const cm = selectedPoint.computed_metrics || {};
                const ex = selectedPoint.exercise;
                const cat = selectedPoint.category;
                let rows = [];

                // 근력 3종(데드/스쿼트/숄프) — 무게 / 반복 / 추정1RM / 상대근력
                const isStrengthCore = cat === 'strength'
                  && ['deadlift', 'squat', 'shoulder_press', 'bench_press'].includes(ex);
                if (isStrengthCore) {
                  // 옛 데이터 호환: 일부 측정은 raw_metrics['1rm_estimated']에 저장되었음
                  const oneRm = cm.one_rm ?? rm['1rm_estimated'];
                  // 상대근력은 cm.relative_strength 우선, 없으면 oneRm/체중으로 계산
                  let relStrength = cm.relative_strength;
                  if (relStrength === undefined && oneRm !== undefined && rm.bw_kg) {
                    relStrength = oneRm / rm.bw_kg;
                  }
                  rows = [
                    rm.weight_kg !== undefined && ['무게', `${rm.weight_kg}kg`],
                    rm.reps !== undefined && ['반복', `${rm.reps}회`],
                    oneRm !== undefined && ['추정 1RM', `${Number(oneRm).toFixed(1)}kg`],
                    relStrength !== undefined && ['상대근력', Number(relStrength).toFixed(2)],
                  ];
                }
                // 근지구력 — 시간 / 기록
                else if (cat === 'muscular_endurance') {
                  // AMRAP: 시간(분) + 원본 기록(X.YY) - 변환값은 노출 안 함
                  const isAmrap = ['amrap1', 'amrap2', 'amrap3', 'amrap4'].includes(ex);
                  if (isAmrap) {
                    rows = [
                      (cm.amrap_minutes ?? rm.amrap_minutes) !== undefined
                        && ['시간', `${cm.amrap_minutes ?? rm.amrap_minutes}min`],
                      (cm.amrap_record ?? rm.amrap_record) !== undefined
                        && ['기록', `${cm.amrap_record ?? rm.amrap_record}`],
                    ];
                  } else {
                    // 푸시업/싯업/플랭크 등 — 시간(초) + 기록(횟수)
                    const sec = rm.seconds ?? rm.duration_seconds ?? rm.time_seconds;
                    // 시간 데이터가 raw_metrics에 없으면 종목별 기본 측정 시간을 폴백으로 사용 (모든 푸시업/싯업은 3분 측정 표준).
                    const DEFAULT_MINUTES = { pushup: 3, situp: 3 };
                    const formatTime = (s) => {
                      if (s === undefined || s === null) return null;
                      if (s % 60 === 0) return `${s / 60}min`;
                      return `${Math.floor(s / 60)}분 ${s % 60}초`;
                    };
                    const timeLabel = sec !== undefined
                      ? (formatTime(sec) || `${sec}초`)
                      : (DEFAULT_MINUTES[ex] ? `${DEFAULT_MINUTES[ex]}min` : null);
                    const recordCell = rm.reps !== undefined
                      ? `${rm.reps}회`
                      : (rm.input_load_kg !== undefined ? `${rm.input_load_kg}kg` : null);
                    rows = [
                      timeLabel !== null && ['시간', timeLabel],
                      recordCell !== null && ['기록', recordCell],
                      // 바벨 근지구력은 기준중량 보조정보로 추가 표시
                      cm.base_load_kg !== undefined && [
                        '기준중량',
                        `${cm.base_load_kg}kg${cm.load_diff_pct !== undefined ? ` (${cm.load_diff_pct > 0 ? '+' : ''}${cm.load_diff_pct}%)` : ''}`,
                      ],
                    ];
                  }
                }
                // 심폐지구력 - 로잉(Performance Time 기반) — 거리/시간/퍼포먼스 기록
                // (최종 점수는 카드 상단에 큰 글씨로 이미 표시됨. base_score/weight_bonus 같은
                //  내부 계산 데이터는 회원 앱에서 표시하지 않는다.)
                else if (cat === 'cardio' && ex === 'rowing') {
                  const perfFmt = cm.performance_time_formatted
                    ?? (cm.performance_time_seconds !== undefined
                          ? `${Math.floor(cm.performance_time_seconds / 60)}:${String(Math.round(cm.performance_time_seconds % 60)).padStart(2, '0')}`
                          : null);
                  const durationLabel = rm.duration_seconds
                    ? `${Math.floor(rm.duration_seconds / 60)}분 ${rm.duration_seconds % 60}초`
                    : null;
                  rows = [
                    rm.distance_m && ['거리', `${rm.distance_m}m`],
                    durationLabel && ['시간', durationLabel],
                    perfFmt && ['퍼포먼스 기록', perfFmt],
                  ];
                }
                // 심폐지구력 - 러닝 — 거리/시간/VO2max (기존 유지)
                else if (cat === 'cardio' && ex === 'running') {
                  const durationLabel = rm.duration_seconds
                    ? `${Math.floor(rm.duration_seconds / 60)}분 ${rm.duration_seconds % 60}초`
                    : null;
                  rows = [
                    rm.distance_m && ['거리', `${rm.distance_m}m`],
                    durationLabel && ['시간', durationLabel],
                    cm.vo2 && ['VO2max', `${cm.vo2}`],
                  ];
                }
                // 기타 카테고리 — 기존 로직 유지
                else {
                  rows = [
                    rm.weight_kg && cat !== 'inbody' && ['무게', `${rm.weight_kg}kg`],
                    rm.reps && ['반복', `${rm.reps}회`],
                    rm.input_load_kg && ['사용중량', `${rm.input_load_kg}kg`],
                    rm.seconds && ['유지시간', `${rm.seconds}초`],
                    rm.distance_m && ['거리', `${rm.distance_m}m`],
                    rm.duration_seconds && ['시간', `${Math.floor(rm.duration_seconds / 60)}분 ${rm.duration_seconds % 60}초`],
                    rm.time_seconds && ['시간', `${Math.floor(rm.time_seconds / 60)}분 ${rm.time_seconds % 60}초`],
                    (rm.weight || (rm.weight_kg && cat === 'inbody')) && ['체중', `${rm.weight || rm.weight_kg}kg`],
                    rm.body_fat_pct && ['체지방률', `${rm.body_fat_pct}%`],
                    rm.body_fat_percentage && ['체지방률', `${rm.body_fat_percentage}%`],
                    rm.skeletal_muscle_kg && ['골격근량', `${rm.skeletal_muscle_kg}kg`],
                    rm.skeletal_muscle_mass && ['골격근량', `${rm.skeletal_muscle_mass}kg`],
                    rm.inbody_score && ['인바디점수', `${rm.inbody_score}`],
                    rm.posture_score && ['자세점수', `${rm.posture_score}`],
                    cm.one_rm && ['1RM 추정', `${cm.one_rm}kg`],
                    cm.relative_strength && ['상대근력', Number(cm.relative_strength).toFixed(2)],
                    cm.vo2 && ['VO2max', `${cm.vo2}`],
                  ];
                }

                const filtered = rows.filter(Boolean);
                if (filtered.length === 0) return null;
                return (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-[#1A1D23] mt-3 pt-3">
                    {filtered.map(([label, value], i) => (
                      <div key={i} className="flex justify-between items-baseline gap-2 text-xs min-w-0">
                        <span className="text-[#52525B] flex-shrink-0">{label}</span>
                        <span className="text-[#A1A1AA] font-medium truncate text-right">{value}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ═══ 6. RECENT MEASUREMENTS ═══ */}
        <div data-testid="recent-measurements" className="bg-[#1A1D23] rounded-2xl p-4" style={anim(0.3)}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white text-sm font-semibold">최근 측정 기록</h2>
            {measurements.length > 0 && (
              <button
                onClick={() => navigate('/app/measurements')}
                className="flex items-center gap-0.5 text-[11px] text-[#71717A] hover:text-[#A1A1AA] active:scale-95 transition"
                data-testid="view-all-measurements-btn"
              >
                전체보기
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            )}
          </div>
          {measurements.length === 0 ? (
            <p className="text-[#2A2E38] text-sm text-center py-8">측정 기록이 없습니다</p>
          ) : (
            <div className="space-y-1">
              {measurements.slice(0, 5).map((m, idx) => {
                const catInfo = CATS[m.category] || CATS.body_composition;
                const isSelected = selectedPoint === m;
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 py-3 px-3 -mx-1 rounded-xl cursor-pointer transition-all duration-200 active:scale-[0.99] ${
                      isSelected ? 'bg-[#22252E]' : 'hover:bg-[#22252E]/50'
                    }`}
                    onClick={() => setSelectedPoint(m)}
                  >
                    {/* Color side line */}
                    <div className="w-0.5 h-8 rounded-full" style={{ backgroundColor: catInfo.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[#A1A1AA] text-sm font-medium truncate">
                        {getCatName(m.category)}
                        {m.exercise && <span className="text-[#3A3F4A] font-normal ml-1.5">· {getExerciseLabel(m.exercise)}</span>}
                      </p>
                      <p className="text-[10px] text-[#3A3F4A] mt-0.5">{formatFull(m.measured_at)}</p>
                    </div>
                    <span className="text-sm font-bold tabular-nums" style={{ color: catInfo.color }}>
                      {m.score?.toFixed(0) || '-'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <BottomNav activeTab="score" />

      {/* ═══ LEVEL INFO BOTTOM SHEET ═══ */}
      {levelInfoOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          data-testid="level-info-overlay"
          onClick={() => setLevelInfoOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in" />

          {/* Sheet */}
          <div
            className="relative w-full max-w-md bg-[#0F1115] border-t border-white/[0.06] rounded-t-3xl px-5 pt-3 pb-8 shadow-[0_-20px_60px_rgba(0,0,0,0.6)] animate-slide-up"
            onClick={(e) => e.stopPropagation()}
            data-testid="level-info-sheet"
          >
            {/* Grabber */}
            <div className="flex justify-center mb-2">
              <div className="w-10 h-1 rounded-full bg-white/15" />
            </div>

            {/* Header */}
            <div className="flex items-start justify-between mb-4 pt-1">
              <div>
                <div className="text-[10px] font-bold tracking-[0.25em] text-[#FF7A00] uppercase mb-1">Level 안내</div>
                <div className="text-white text-lg font-bold">레벨은 어떻게 결정되나요?</div>
              </div>
              <button
                type="button"
                onClick={() => setLevelInfoOpen(false)}
                className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] active:scale-95 transition-all flex items-center justify-center"
                data-testid="level-info-close"
                aria-label="닫기"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
            </div>

            {/* Levels list — 운영자가 기준표에서 설정한 정책 그대로 표시 (높은 레벨부터) */}
            <div className="space-y-2 mb-4">
              {(() => {
                // 사용자 가이드 라벨: 정확히 5단계인 경우에만 매핑, 그 외엔 점수구간 라벨
                const FIVE_TIER_DESC = ['하위 40%', '평균', '평균 이상', '상위 20%', '상위 5%'];
                const sorted = [...scoreLevels]
                  .map((l, i) => ({ ...l, idx: i }))
                  .sort((a, b) => (b.min_score ?? 0) - (a.min_score ?? 0));
                const useFiveTier = scoreLevels.length === 5;
                return sorted.map((lvl) => {
                  const nextMin = scoreLevels[lvl.idx + 1]?.min_score;
                  const rangeLabel = nextMin != null
                    ? `${lvl.min_score} ~ ${nextMin - 1}점`
                    : `${lvl.min_score}점 이상`;
                  const desc = useFiveTier ? FIVE_TIER_DESC[lvl.idx] : rangeLabel;
                  // Match badge color palette used in app
                  const COLORS = ['#EF4444', '#F97316', '#22C55E', '#3B82F6', '#A78BFA', '#EC4899'];
                  const color = COLORS[lvl.idx] || COLORS[COLORS.length - 1];
                  return (
                    <div
                      key={`${lvl.name}-${lvl.idx}`}
                      data-testid={`level-row-${lvl.idx}`}
                      className="flex items-center justify-between bg-[#1A1D23] rounded-xl px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded"
                          style={{ color, backgroundColor: `${color}1A` }}
                        >
                          {lvl.name}
                        </span>
                        <span className="text-white/80 text-sm">{desc}</span>
                      </div>
                      <span className="text-[#71717A] text-xs tabular-nums">
                        {nextMin != null ? `${lvl.min_score}~${nextMin - 1}` : `${lvl.min_score}+`}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Footer note */}
            <div className="border-t border-white/[0.06] pt-3 space-y-1">
              <p className="text-[#A1A1AA] text-xs leading-relaxed">
                Level은 {scoreLevels.length > 0 ? `0~${scoreLevels.length - 1}까지 총 ${scoreLevels.length}단계` : '여러 단계'}로 구성됩니다.
              </p>
              <p className="text-[#A1A1AA] text-xs leading-relaxed">
                회원님의 최근 측정 데이터를 기반으로 자동 계산됩니다.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberScore;
